<template>
    <div class="d-flex justify-content-center mx-0 mx-sm-3 mx-md-4 mx-lg-5">
        <div class="container page-container">
            <div class="row">
                <div class="col-12">
                    <br />
                    <br />
                    <slot name="header"></slot>
                    <!-- Breadcrumbs & Edit Actions -->
                    <div class="row mb-4" v-if="isAuthorView">
                        <div class="col-12">
                            <div class="d-flex">
                                <div class="flex-grow-1">
                                    <router-link :to="{ name: 'Pages' }">
                                        <span
                                            class="icon icon-sm icon-glyphicons-basic-chevron-left mr-1 align-middle"
                                        ></span>
                                        <span class="align-middle">Back to Pages</span>
                                    </router-link>
                                </div>
                                <div class="flex-grow-0 d-flex align-items-center ml-2">
                                    <button class="btn btn-light btn-sm mr-3" @click="openEditLink()">
                                        <span class="icon icon-glyphicons-basic-link mr-1"></span>
                                        <span>View Link</span>
                                    </button>
                                    <loading-status
                                        :loading="saveInProgress"
                                        loaded-text="All Edits Saved"
                                        loading-text="Saving changes..."
                                    ></loading-status>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <!-- Custom Logo Display -->
                <div v-if="customLogoUrl" class="col-12" style="margin-bottom: 32px">
                    <div class="logo-sm d-flex align-items-center rounded-circle logo-circle">
                        <img :src="customLogoUrl" />
                    </div>
                </div>
                <!-- Page Title & Description -->
                <div class="col-12 section-container">
                    <div class="mb-4">
                        <editable-text-field
                            v-model="page.title"
                            editor-class="page-title"
                            class="page-title"
                            :edit-enabled="editingEnabled"
                        ></editable-text-field>
                    </div>
                    <div style="margin-bottom: 35px">
                        <editable-text-field
                            v-model="page.description"
                            class="page-description"
                            editor-class="page-description"
                            placeholder="Add a description of this section..."
                            empty-placeholder="Click to add a description of this page..."
                            editorType="richtext"
                            :edit-enabled="editingEnabled"
                        ></editable-text-field>
                    </div>
                    <!-- Attachments -->
                    <publish-page-attachments
                        v-model="page.attachments"
                        :editing-enabled="editingEnabled"
                    ></publish-page-attachments>
                </div>
                <!-- Sections -->
                <div class="col-12">
                    <div v-for="(section, sectionIndex) in page.sections" :key="sectionIndex" class="section-container">
                        <div class="d-flex">
                            <div class="flex-grow-1">
                                <editable-text-field
                                    v-model="section.title"
                                    editor-class="section-title"
                                    class="section-title"
                                    empty-placeholder="Click to add a title for this section..."
                                    :edit-enabled="editingEnabled"
                                ></editable-text-field>
                            </div>
                            <div class="flex-grow-0" v-if="editingEnabled">
                                <div class="flex-grow-0">
                                    <button class="btn btn-transparent mr-1 p-1" @click="openSectionSettings(section)">
                                        <span class="icon icon-glyphicons-basic-cogwheel"></span>
                                    </button>
                                    <button
                                        class="btn btn-transparent p-1"
                                        @click="shiftItemIndex(sectionIndex, 1, page.sections)"
                                    >
                                        <span class="icon icon-glyphicons-basic-arrow-down"></span>
                                    </button>
                                    <button
                                        class="btn btn-transparent mr-1 p-1"
                                        @click="shiftItemIndex(sectionIndex, -1, page.sections)"
                                    >
                                        <span class="icon icon-glyphicons-basic-arrow-up"></span>
                                    </button>
                                    <button
                                        class="btn btn-transparent mr-1 p-1"
                                        @click="openRemoveSection(sectionIndex)"
                                    >
                                        <span class="icon icon-glyphicons-basic-bin"></span>
                                    </button>
                                </div>
                            </div>
                        </div>
                        <hr />
                        <editable-text-field
                            v-model="section.description"
                            editor-class="section-description"
                            editor-type="richtext"
                            class="section-description pb-2"
                            placeholder="A description for this page to show to learners..."
                            empty-placeholder="Click to add a description of this section..."
                            :edit-enabled="editingEnabled"
                            :class="{ 'pb-3 pt-2': section.description || editingEnabled }"
                        ></editable-text-field>
                        <!-- Attachments -->
                        <publish-page-attachments
                            v-model="section.attachments"
                            :editing-enabled="editingEnabled"
                            :class="{ 'pt-1 pb-1': section.attachments.length || editingEnabled }"
                        ></publish-page-attachments>
                        <!-- Item List -->
                        <draggable
                            v-model="section.library_items"
                            v-bind="dragOptions"
                            :disabled="!editingEnabled"
                            class="grid flex-grow-1 justify-content-center section-cases"
                            tag="ul"
                        >
                            <li
                                class="d-flex flex-column item draggable-item"
                                v-for="(item, index) in section.library_items"
                                :key="item.id"
                                :ref="'item-' + item.id"
                            >
                                <div
                                    class="card case-card rounded-lg border-0 mb-2"
                                    role="button"
                                    aria-label="Open Scenario Start Window"
                                    @click="openPlay(section, item, index)"
                                >
                                    <img
                                        v-if="item.case.cover_image"
                                        :src="item.case.cover_image.signed_url"
                                        class="image-cover rounded-lg"
                                        :alt="'Cover image for scenario titled' + item.case.cover_image_alt_text"
                                    />
                                    <div v-else class="d-flex w-100 h-100 align-items-center justify-content-center">
                                        <span
                                            class="h5 mb-0 px-2 font-weight-bold text-center max-two-lines cover-placeholder"
                                        >
                                            {{ getItemTitle(item) }}
                                        </span>
                                    </div>
                                    <div
                                        class="card-img-overlay d-flex align-items-center rounded-lg img-overlay"
                                        v-if="itemIsPlayable(section, index)"
                                    >
                                        <div
                                            class="h1 card-title mx-auto mb-0 play-button d-flex flex-column align-items-center"
                                        >
                                            <span class="icon icon-glyphicons-basic-circle-empty-play"></span>
                                            <br />
                                            <span v-if="editingEnabled" class="medium">
                                                <span class="icon icon-100 icon-glyphicons-basic-arrow-left"></span>
                                                <span>drag to reorder</span>
                                                <span class="icon icon-100 icon-glyphicons-basic-arrow-right"></span
                                            ></span>
                                            <span v-else class="resume-text">START</span>
                                        </div>
                                    </div>
                                    <div
                                        class="card-img-overlay d-flex align-items-center rounded-lg img-overlay img-locked"
                                        v-else
                                    >
                                        <div
                                            class="h1 card-title mx-auto mb-0 play-button d-flex flex-column align-items-center"
                                        >
                                            <span class="icon icon-glyphicons-basic-lock lock-icon"></span>
                                        </div>
                                    </div>
                                </div>
                                <div
                                    v-if="
                                        getSessionByCaseId(item.case.id) &&
                                        !caseIsCompleted(item.case.id) &&
                                        getSessionProgressPercent(item.case.id) !== 100
                                    "
                                    class="progress mt-1 mb-2"
                                    style="height: 4px"
                                >
                                    <div
                                        class="progress-bar bg-blue"
                                        role="progressbar"
                                        :style="getSessionProgressStyle(item.case.id)"
                                        :aria-valuenow="getSessionProgressPercent(item.case.id)"
                                        aria-valuemin="0"
                                        aria-valuemax="100"
                                        aria-label="Session progress bar"
                                    ></div>
                                </div>
                                <div class="d-flex">
                                    <span
                                        v-show="caseIsCompleted(item.case.id)"
                                        class="badge badge-success align-middle complete-text mt-1"
                                        >Complete</span
                                    >
                                </div>
                                <div class="d-flex align-items-center">
                                    <div class="flex-grow-1">
                                        <p class="max-one-line item-title">
                                            {{ getItemTitle(item) }}
                                        </p>
                                    </div>
                                    <div class="flex-grow-0 case-remove-btn" v-if="editingEnabled">
                                        <button
                                            class="btn btn-icon ml-1 mr-1 p-0"
                                            @click="removeItemByIndex(index, section)"
                                        >
                                            <span class="icon icon-100 icon-glyphicons-basic-bin"></span>
                                        </button>
                                    </div>
                                </div>
                                <p class="text-secondary small mb-0 max-two-lines">
                                    {{ getItemDescription(item) }}
                                </p>
                            </li>
                            <li v-if="editingEnabled" slot="footer" class="d-flex flex-column item">
                                <div
                                    class="image-container d-flex align-items-center justify-content-center placeholder add-case-placeholder rounded-lg"
                                    @click="openAddItem(section)"
                                >
                                    <span class="icon icon-100 icon-glyphicons-basic-plus icon-mr align-middle"></span>
                                    <span class="align-middle">Scenario</span>
                                </div>
                            </li>
                        </draggable>
                    </div>
                    <div
                        v-if="editingEnabled"
                        @click="addSection()"
                        type="button"
                        class="placeholder section-placeholder d-flex justify-content-center align-items-center rounded-lg"
                    >
                        <div class="d-flex flex-column align-items-center">
                            <div class="mb-1">
                                <span class="icon icon-100 icon-glyphicons-basic-plus icon-mr align-middle"></span>
                                <span class="align-middle">Section</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <!-- Add Case Modal -->
            <base-modal id="add-modal" :loading="modalLoading" :centered="false">
                <template slot="title"> Add Scenario </template>
                <template>
                    <library-item-search
                        @selected="handleItemSelection($event)"
                        :clear-after-selection="true"
                        :constrain-width="false"
                        :item-types="['owned_case', 'community_reference']"
                        placeholder="Search for a scenario..."
                        ref="libraryItemSearch"
                    ></library-item-search>
                </template>
                <template slot="footer">
                    <button class="btn btn-secondary" type="button" data-dismiss="modal">Close</button>
                </template>
            </base-modal>
            <!-- Remove Section Modal -->
            <base-modal id="remove-section-modal" :loading="modalLoading" :centered="true">
                <template slot="title">Remove Section</template>
                <template>
                    <p class="my-1">Remove section?</p>
                </template>
                <template slot="footer">
                    <button class="btn btn-secondary" type="button" data-dismiss="modal">Close</button>
                    <button class="btn btn-danger" type="button" @click="removeSection()">Remove</button>
                </template>
            </base-modal>
            <!-- Section Settings Modal -->
            <base-modal id="section-settings-modal" :loading="modalLoading" :centered="true">
                <template slot="title">
                    <span class="icon icon-100 icon-glyphicons-basic-cogwheel align-middle mr-1"></span>
                    <span class="align-middle">Section Settings</span>
                </template>
                <template>
                    <base-form :fields="sectionSettingsFields" v-model="selectedSectionCopy"></base-form>
                </template>
                <template slot="footer">
                    <button class="btn btn-secondary" type="button" data-dismiss="modal">Close</button>
                    <button class="btn btn-primary" type="button" @click="saveSectionSettings()">Save</button>
                </template>
            </base-modal>
            <!-- Play Modal -->
            <base-modal id="play-modal" :centered="true">
                <template slot="title"> Start Scenario </template>
                <template>
                    <div v-if="selectedItem">
                        <p class="my-2 modal-header-text text-secondary">Title</p>
                        <p class="case-description-long mb-4">{{ getItemTitle(selectedItem) }}</p>
                        <p class="my-2 modal-header-text text-secondary" v-show="getItemDescription(selectedItem)">
                            Description
                        </p>
                        <p class="case-description-long">{{ getItemDescription(selectedItem) }}</p>
                        <div v-if="selectedItem.is_community_published" class="pt-3">
                            <p class="text-secondary small mb-1">
                                <router-link
                                    :to="{ name: 'CommunityCaseDetail', params: { id: selectedItem.case.id } }"
                                    data-dismiss="modal"
                                    >{{ getItemTitle(selectedItem) }}</router-link
                                >
                                is licensed as an open educational resource under
                                <a
                                    href="https://creativecommons.org/licenses/by-sa/4.0/"
                                    target="_blank"
                                    rel="noopener"
                                >
                                    Creative Commons Attribution-ShareAlike 4.0 International (CC BY-SA 4.0)</a
                                >.
                            </p>
                        </div>
                    </div>
                </template>
                <template slot="footer">
                    <router-link
                        v-if="selectedItem && editingEnabled"
                        class="btn btn-sm btn-link"
                        type="button"
                        data-dismiss="modal"
                        :to="{ name: 'CaseSettings', params: { id: selectedItem.case.id } }"
                        >View in Editor</router-link
                    >
                    <button class="btn btn-secondary" type="button" data-dismiss="modal">Close</button>
                    <button
                        v-show="selectedItemCurrentSession"
                        class="btn btn-light"
                        type="button"
                        :disabled="editingEnabled"
                        @click="startNewSession()"
                    >
                        <span class="icon icon-glyphicons-basic-restart"></span>
                        Start Over
                    </button>
                    <button
                        v-show="selectedItemCurrentSession"
                        class="btn btn-brand"
                        type="button"
                        :disabled="editingEnabled"
                        @click="resumeSession()"
                    >
                        <span class="icon icon-glyphicons-basic-play"></span>
                        Resume
                    </button>
                    <button
                        v-show="!selectedItemCurrentSession"
                        class="btn btn-brand"
                        type="button"
                        :disabled="isAuthorView || startIsLoading"
                        @click="startNewSession()"
                    >
                        <span
                            v-show="startIsLoading"
                            class="spinner-grow spinner-grow-sm continue-btn-spinner"
                            role="status"
                            aria-hidden="true"
                        ></span>
                        <span class="icon icon-glyphicons-basic-play"></span>
                        Start
                    </button>
                </template>
            </base-modal>
            <!-- Scenario Locked Modal -->
            <base-modal id="locked-modal" :loading="modalLoading" :centered="true">
                <template slot="title">
                    <span class="icon icon-100 icon-glyphicons-basic-lock align-middle mr-1"></span>
                    <span class="align-middle">Scenario Locked</span>
                </template>
                <template>
                    <p class="my-2">Complete the previous scenario in the list before attempting this one.</p>
                </template>
                <template slot="footer">
                    <button class="btn btn-secondary" type="button" data-dismiss="modal">Close</button>
                </template>
            </base-modal>
            <!-- Link Modal -->
            <publish-page-link-modal
                v-model="page"
                :read-only="!editingEnabled"
                ref="pageLinkModal"
            ></publish-page-link-modal>
        </div>
    </div>
</template>

<script>
import http from "@/axios.js"
import draggable from "vuedraggable"
import BaseModal from "@/components/BaseModal"
import BaseForm from "@/components/BaseForm"
import EditableTextField from "@/components/EditableTextField"
import LoadingStatus from "@/components/LoadingStatus.vue"
import PublishPageLinkModal from "@/components/PublishPageLinkModal"
import PublishPageAttachments from "@/components/PublishPageAttachments"
import LibraryItemSearch from "@/components/LibraryItemSearch"
import debounce from "lodash.debounce"

export default {
    name: "PublishPage",
    components: {
        BaseModal,
        BaseForm,
        EditableTextField,
        LoadingStatus,
        PublishPageLinkModal,
        PublishPageAttachments,
        LibraryItemSearch,
        draggable,
    },
    props: {
        // Either "author" or "learner".
        viewType: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            page: {
                title: null,
                description: null,
                sections: [],
                attachments: [],
                organization: {
                    id: null,
                },
            },
            editingEnabled: false,
            selectedItem: null,
            selectedSection: null,
            selectedSectionCopy: null,
            modalLoading: false,
            startIsLoading: false,
            saveInProgress: false,
            viewInitalized: false,
            selectedItemIndex: false,
            sectionSettingsFields: [
                {
                    name: "Require that learners complete scenarios in order",
                    subtext:
                        "When checked, the next scenario in sequence will become unlocked when the learner completes the previous one.",
                    type: "switch",
                    key: "enforce_sequential_completion",
                },
            ],
            dragOptions: {
                animation: 300,
                ghostClass: "ghost",
                direction: "horizontal",
                draggable: ".draggable-item",
                dragClass: "sortable-drag",
                chosenClass: "sortable-chosen",
                delay: 500,
                delayOnTouchOnly: true,
            },
            viewerId: null,
            sessions: [],
            completedCaseIds: [],
        }
    },
    computed: {
        pageId() {
            return this.$route.params.id
        },
        shareLinkId() {
            return this.$route.params.shareLinkID
        },
        pageTitle() {
            return this.page.title
        },
        selectedItemCurrentSession() {
            if (!this.selectedItem) {
                return false
            }
            return this.getSessionByCaseId(this.selectedItem.case.id)
        },
        creativeCommonsUrl() {
            return this.$store.getters.creativeCommonsLicenseUrl
        },
        currentOrganizationId() {
            return this.$store.getters.currentOrganizationId
        },
        isAuthorView() {
            return this.viewType === "author"
        },
        customLogoUrl() {
            if (!this.page.organization.id || !this.page.organization.logo_image) {
                return null
            }
            return this.page.organization.logo_image.signed_url
        },
    },
    watch: {
        // Auto save on page edits.
        page: {
            handler(newValue, oldValue) {
                if (!this.viewInitalized || !oldValue.id) {
                    return
                }
                this.savePage()
            },
            deep: true,
        },
        // Update the window title when the page title is edited.
        pageTitle: function (newValue) {
            if (newValue == null) {
                return
            }
            document.title = `Viewing: ${newValue}`
        },
    },
    methods: {
        // Fetch data to edit the page.
        fetchEditorData() {
            let vm = this
            this.$store.dispatch("addLoad")
            http.get(`/api/pages/${this.pageId}/`)
                .then((response) => {
                    vm.page = response.data
                    vm.viewInitalized = true
                    vm.editingEnabled = !response.data.read_only
                })
                .finally(() => vm.$store.dispatch("removeLoad"))
        },
        // Fetch data to view the page.
        async fetchViewerData() {
            let vm = this
            // Fetch page details.
            this.$store.dispatch("addLoad")
            http.get(`/api/students/pages/${this.pageId}/`)
                .then(function (response) {
                    vm.page = response.data
                    vm.viewInitalized = true
                    // Store the page in the datastore so that we can read metadata from it in other
                    // places, like the owning-organization's custom logo.
                    vm.$store.commit("setPage", response.data)
                    // Scroll to the last page item accessed by the user, if one is stored.
                    vm.$nextTick(() => {
                        let lastPageItemId = vm.$store.getters.lastPageItemId
                        let lastPageItemRef = vm.$refs[`item-${lastPageItemId}`][0]
                        if (lastPageItemId && lastPageItemRef) {
                            lastPageItemRef.scrollIntoView({
                                behavior: "smooth",
                                block: "center",
                            })
                        }
                    })
                })
                .finally(() => vm.$store.dispatch("removeLoad"))

            // Fetch the viewer ID for the user - this will allow us to fetch or start sessions.
            let viewerId = await this.fetchViewerId()

            // Fetch session activity history.
            this.$store.dispatch("addLoad")
            http.get(`/api/students/view/${viewerId}/sessions/`)
                .then(function (response) {
                    vm.sessions = response.data.sessions
                    vm.completedCaseIds = response.data.completed_case_ids
                })
                .finally(() => vm.$store.dispatch("removeLoad"))
        },
        // Fetch the viewer id for the user.
        fetchViewerId() {
            let vm = this
            return new Promise((resolve) => {
                http.get(`/api/students/pages/${vm.pageId}/viewer/`).then(function (response) {
                    vm.viewerId = response.data.id
                    resolve(response.data.id)
                })
            })
        },
        // Add a new section to the page.
        addSection() {
            this.page.sections.push({
                title: "Untitled Section",
                description: "",
                library_items: [],
                attachments: [],
            })
        },
        // Open the remove section modal.
        openRemoveSection(index) {
            this.selectedItemIndex = index
            $("#remove-section-modal").modal("show")
        },
        // Remove a section.
        removeSection() {
            this.removeItem(this.selectedItemIndex, this.page.sections)
            this.selectedItemIndex = null
            $("#remove-section-modal").modal("hide")
        },
        // Open the section settings modal.
        openSectionSettings(section) {
            this.selectedSection = section
            this.selectedSectionCopy = Object.assign({}, section)
            $("#section-settings-modal").modal("show")
        },
        // Save section settings.
        saveSectionSettings() {
            this.sectionSettingsFields.forEach((field) => {
                this.selectedSection[field.key] = this.selectedSectionCopy[field.key]
            })
            $("#section-settings-modal").modal("hide")
        },
        // Open the add-item modal.
        openAddItem(section) {
            this.selectedItem = null
            this.selectedSection = section
            $("#add-modal").modal("show")
            let vm = this
            // Need to add a timeout before bringing the searchbar into focus because the modal
            // doesn't show immediately (because of the animation).
            window.setTimeout(() => {
                vm.$refs.libraryItemSearch.focus()
            }, 180)
        },
        // Add the selected item to the section - called when an item is selected in searchbar.
        handleItemSelection(item) {
            this.selectedSection.library_items.push(item)
            $("#add-modal").modal("hide")
        },
        // Remove an item by index.
        removeItemByIndex(index, section) {
            this.removeItem(index, section.library_items)
        },
        // Save the page, with debouncing.
        savePage() {
            this.saveInProgress = true
            this.savePageDebounced()
        },
        savePageDebounced: debounce(async function () {
            if (!this.editingEnabled) {
                return
            }
            let vm = this
            http.put(`/api/pages/${this.pageId}/edit/`, this.page).finally(() => (vm.saveInProgress = false))
        }, 300),
        // Shift the position of an item in an array.
        shiftItemIndex(index, increment, arr) {
            // Don't do anything if case is already at the start or end of the list.
            if ((index == 0 && increment == -1) || (index + 1 >= arr.length && increment == 1)) {
                return
            }
            // Remove the element from the list.
            let item = arr.splice(index, 1)[0]
            // Put it back at the new position.
            arr.splice(index + increment, 0, item)
        },
        // Remove an item from an array by its index.
        removeItem(index, arr) {
            arr.splice(index, 1)
        },
        // Open the play modal.
        openPlay(section, item, index) {
            if (!this.itemIsPlayable(section, index)) {
                $("#locked-modal").modal("show")
                return
            }
            this.selectedItem = item
            $("#play-modal").modal("show")
        },
        // Start a new learner session for the selected item.
        startNewSession() {
            this.startIsLoading = true
            let selectedItemId = this.selectedItem.id
            let vm = this
            http.post(`/api/students/view/${this.viewerId}/items/${selectedItemId}/start/`)
                .then((response) => {
                    $("#play-modal").modal("hide")
                    vm.navigateToSession(response.data.sessionId)
                    vm.$store.commit("setLastPageItemId", selectedItemId)
                })
                .finally(() => {
                    vm.startIsLoading = false
                })
        },
        // Resume a previously started learner session for the selected item.
        resumeSession() {
            let currenSession = this.getSessionByCaseId(this.selectedItem.case.id)
            $("#play-modal").modal("hide")
            this.$store.commit("setLastPageItemId", this.selectedItem.id)
            this.navigateToSession(currenSession.id)
        },
        // Navigate to a session by its ID.
        navigateToSession(sessionId) {
            this.$router.push({
                name: "PageViewerShareSession",
                params: {
                    pageID: this.pageId,
                    sessionID: sessionId,
                    shareLinkID: this.shareLinkId,
                },
            })
        },
        // Get the display-title of an item.
        getItemTitle(libraryItem) {
            if (libraryItem.item_type === "owned_case") {
                return libraryItem.case.public_title
            } else {
                return libraryItem.case.community_title
            }
        },
        // Get the display-description of an item.
        getItemDescription(libraryItem) {
            if (libraryItem.item_type === "owned_case") {
                return libraryItem.case.public_description
            } else {
                return libraryItem.case.community_description
            }
        },
        // Returns an object formatted for style binding - used to control progress bar length.
        getSessionProgressStyle(caseId) {
            return {
                width: this.getSessionProgressPercent(caseId) + "%",
            }
        },
        // Get session progress as a percentage.
        getSessionProgressPercent(caseId) {
            let session = this.getSessionByCaseId(caseId)
            let progress = session.progress_percent
            if (progress == 0) {
                progress = 1
            }
            return progress
        },
        // Find a session by its associated case ID.
        getSessionByCaseId(caseId) {
            return this.sessions.find((s) => s.case.id == caseId)
        },
        // Determine whether a case has been completed by the user.
        caseIsCompleted(caseId) {
            return this.completedCaseIds.includes(caseId)
        },
        // Returns true if a case can be played by a viewer.
        itemIsPlayable(section, index) {
            if (this.editingEnabled) {
                return true
            }
            if (index == 0) {
                return true
            }
            if (!section.enforce_sequential_completion) {
                return true
            }
            // Make sure the last case has been completed first.
            let lastItem = section.library_items[index - 1]
            return this.caseIsCompleted(lastItem.case.id)
        },
        // Open the page link modal.
        openEditLink() {
            this.$refs.pageLinkModal.open()
        },
    },
    mounted() {
        if (this.viewType === "author") {
            this.fetchEditorData()
        } else {
            this.fetchViewerData()
        }
    },
}
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables.scss";
@import "@/assets/styles/page.scss";

$card-height: 180px;

.page-container {
    @media (min-width: ($breakpoint-xxl + 100)) {
        max-width: 1300px;
    }
    max-width: 100%;
}

.section-placeholder {
    height: 200px;
}

.add-case-placeholder {
    height: $card-height;
}

.section-container {
    margin-bottom: 4.25rem;
}

.section-cases {
    margin-top: 1rem;
}

hr {
    margin-top: 8px;
    margin-bottom: 8px;
}

.edit-switch {
    transform: scale(1.2);
}

.edit-switch-label {
    font-weight: 500;
    min-width: 110px;
}

.grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    grid-template-rows: 1fr;
    padding-inline-start: 0px;
    grid-gap: 30px;
    @media (min-width: $breakpoint-xl) {
        grid-gap: 36px;
    }
}

.item {
    list-style-type: none;
    &:hover {
        .case-remove-btn {
            display: block;
        }
    }
    .case-remove-btn {
        display: none;
    }
}

.img-overlay {
    &:hover {
        background-color: rgba(0, 0, 0, 0.5);
        .play-button {
            color: #fff;
        }
    }
    transition: background-color 0.25s;
}

.img-locked {
    background-color: rgba(0, 0, 0, 0.4);
    .play-button {
        color: #fff;
    }
}

.item-title {
    margin-top: 2px;
    margin-bottom: 3px;
    font-size: 14.5px;
    line-height: 1.5;
}

.case-card {
    box-shadow: $box-shadow-md;
    background-color: #fff;
    height: $card-height;
    cursor: pointer;
}

.image-cover {
    object-fit: cover;
    overflow: hidden;
    height: 100%;
    width: 100%;
}

.play-button {
    color: transparent;
    transition: color 0.25s;
    line-height: 12px; // setting this to control child line break elements
    .icon {
        font-size: 3rem;
    }
}

.resume-text {
    font-size: 14px;
    font-weight: 500;
}

.modal-header-text {
    font-weight: 700;
    font-size: 12px;
    text-transform: uppercase;
}

.lock-icon {
    font-size: 2rem !important;
}

.logo-circle {
    overflow: hidden;
    background-color: $white;
    border: solid 1px $gray-200;
    height: 85px;
    width: 85px;
    cursor: default;
    img {
        padding: 2px;
        width: 100%;
    }
}
</style>

<style lang="scss">
$max-width: 720px;

// Keep these classes un-scoped so that we can pass them into the editable text
// child components and use them there too, to keep things consistent.
.page-title {
    font-size: 1.7rem;
    font-weight: 700;
    max-width: 100%;
    @media (min-width: $max-width) {
        max-width: $max-width;
    }
}

.page-description {
    width: 840px;
    max-width: 100%;
    @media (min-width: $max-width) {
        max-width: $max-width;
    }
}

.section-title {
    font-weight: 700;
    font-size: 20px;
    max-width: 100%;
    @media (min-width: $max-width) {
        max-width: $max-width;
    }
}

.section-description {
    max-width: 100%;
    @media (min-width: $max-width) {
        max-width: $max-width;
    }
}

tbody {
    background-color: white;
}
</style>